import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { apiService } from '../services/apiService';
import { genericCopy } from '../utils/userFacingCopy';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiReady, setApiReady] = useState(false);
  const [apiCheckLoading, setApiCheckLoading] = useState(true);
  const [apiCheckError, setApiCheckError] = useState(false);

  const checkApi = useCallback(async () => {
    try {
      setApiCheckLoading(true);
      setApiCheckError(false);
      await apiService.checkApiReady();
      setApiReady(true);
    } catch {
      setApiReady(false);
      setApiCheckError(true);
    } finally {
      setApiCheckLoading(false);
    }
  }, []);

  useEffect(() => {
    checkApi();
  }, [checkApi]);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);

      authService.clearAuthState();
      await authService.login();
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Login failed:', err);
      setError(genericCopy.loginFailed);
    } finally {
      setIsLoading(false);
    }
  };

  const fontFamily = "'Outfit', sans-serif";

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative"
      style={{
        backgroundColor: '#010027',
        fontFamily,
      }}
    >
      <img
        src="/alula-login-logo.png"
        alt="Alula"
        className="absolute bottom-6 right-6 w-10 h-10 object-contain opacity-90"
      />
      <div
        className="max-w-md w-full rounded-xl px-8 py-10 shadow-xl"
        style={{
          backgroundColor: '#1A3646',
          fontFamily,
        }}
      >
        <div className="space-y-6">
          <h1
            className="text-center text-6xl font-semibold text-white lowercase tracking-tight"
            style={{ fontFamily, color: '#FFFFFF' }}
          >
            alula <span className="uppercase">IT</span> helpdesk
          </h1>
          <p
            className="text-center text-sm leading-relaxed"
            style={{ color: '#BDC0C4' }}
          >
            {!apiCheckLoading && apiCheckError
              ? genericCopy.signInUnavailable
              : (
                <>
                  Use your <strong>work</strong> or <strong>school</strong> account managed by Microsoft to sign in.
                </>
              )}
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {apiCheckLoading && (
            <div
              className="flex flex-col items-center justify-center gap-3 py-10"
              aria-busy="true"
              aria-live="polite"
            >
              <div className="h-8 w-8 rounded-full border-2 border-white/40 border-t-white animate-spin" />
              <p className="text-sm" style={{ color: '#BDC0C4' }}>
                {genericCopy.checkingService}
              </p>
            </div>
          )}

          {!apiCheckLoading && apiCheckError && (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 px-4 py-3">
                <p className="text-sm text-amber-100">{genericCopy.signInOptions}</p>
              </div>
              <button
                type="button"
                onClick={checkApi}
                disabled={apiCheckLoading}
                className="w-full flex items-center justify-center gap-2 py-3 px-5 rounded-lg text-sm font-normal transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#1A3646] focus:ring-white/30 border border-white/30 text-white hover:bg-white/10"
                style={{ fontFamily }}
              >
                {apiCheckLoading ? genericCopy.retrying : genericCopy.retry}
              </button>
            </div>
          )}

          {!apiCheckLoading && apiReady && !apiCheckError && (
            <>
              {error && (
                <div className="rounded-lg border border-red-400/50 bg-red-500/10 px-4 py-3">
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}
              <button
                type="button"
                onClick={handleLogin}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 py-3 px-5 rounded-lg text-sm font-normal transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#1A3646] focus:ring-white/30"
                style={{
                  backgroundColor: '#FFFFFF',
                  color: '#333333',
                  fontFamily,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                }}
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 rounded-full border-2 border-[#333333] border-t-transparent animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <div className="flex shrink-0">
                      <div className="grid grid-cols-2 gap-px">
                        <div className="w-2.5 h-2.5 bg-[#F25022]" aria-hidden />
                        <div className="w-2.5 h-2.5 bg-[#7FBA00]" aria-hidden />
                        <div className="w-2.5 h-2.5 bg-[#00A4EF]" aria-hidden />
                        <div className="w-2.5 h-2.5 bg-[#FFB900]" aria-hidden />
                      </div>
                    </div>
                    <span>Sign in with Microsoft</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
